import type { MotionProject } from './motion-model';
// Fixed creative routes. Quotes authorize only this batch, never the whole film.
export function motionDirectionSummary(project: any): string {
  const direction = project.motion_direction;
  if (!direction?.ready) return 'Review motion direction after rebuilding the storyboard.';
  const rows = project.providers?.video?.capabilities as {family: string; label: string}[] | undefined;
  if (rows?.length) {
    // The models the shots really use (server-resolved, #71), not the old fixed routes.
    const counts: Record<string, number> = {};
    for (const scene of project.scenes || []) {
      const family = scene.motion_direction?.provider;
      if (!family) continue;
      const label = rows.find((row) => row.family === family)?.label || family;
      counts[label] = (counts[label] || 0) + 1;
    }
    if (Object.keys(counts).length)
      return `${Object.entries(counts).map(([name, count]) => `${count} shots · ${name}`).join(' | ')}. Lip-sync follows after animation review. No automatic model substitution.`;
  }
  if (Object.keys(direction.scene_provider_overrides || {}).length) {
    const counts: Record<string, number> = {};
    for (const id of direction.scene_ids) {
      const provider = direction.scene_provider_overrides[id] || (direction.dance_scene_ids.includes(id) ? 'kling' : 'wan');
      const label = ({kling: 'Kling 3', wan: 'Wan 2.7', seedance: 'Seedance 2.5', grok: 'Grok Imagine 1.5'} as Record<string, string>)[provider] || provider;
      counts[label] = (counts[label] || 0) + 1;
    }
    return `${Object.entries(counts).map(([name, count]) => `${count} shots · ${name}`).join(' | ')}. Lip-sync follows after animation review. No automatic model substitution.`;
  }
  const dance = direction.dance_scene_ids.length;
  return `${dance} dance shots · Kling 3 | ${direction.scene_ids.length - dance} quiet shots · Wan 2.7. Lip-sync follows after animation review. No automatic model substitution.`;
}

export function fixedMotionRequest(project: any): any {
  const quote = project.motion_generation_quote;
  if (!quote?.ready || !quote.scene_ids?.length) throw new Error(quote?.error || 'No motion batch is ready.');
  return {
    scene_ids: quote.scene_ids,
    motion_quote_signature: quote.signature,
    motion_strategy: 'uniform',
    replace_existing: true,
    allow_economy_fallback: false,
  };
}

export function motionBatchConfirmation(project: any): string {
  const quote = project.motion_generation_quote;
  fixedMotionRequest(project);
  const counts: Record<string, number> = {};
  for (const shot of quote.scenes || []) counts[shot.model] = (counts[shot.model] || 0) + 1;
  const models = Object.keys(counts).length
    ? Object.entries(counts).map(([model, count]) => `${count} shots: ${model}`).join(' · ')
    : `${quote.hero_scene_count} dance shots: Kling 3 · ${quote.support_scene_count} quiet shots: Wan 2.7.`;
  return `Generate ${quote.scene_ids.length} animation clips?\n${models}\n${quote.estimated_credits} credits estimated for THIS batch.\n${quote.later_count} clips remain for a separate confirmation.\n\nNo image regeneration, no automatic provider substitution, no lip-sync yet. Existing images and clips stay preserved.`;
}

const motionModelNames: Record<string, string> = {kling: 'Kling 3', wan: 'Wan 2.7', seedance: 'Seedance 2.5 · 720p', grok: 'Grok Imagine 1.5 · 720p'};

function recommendedMotionProvider(project: any, sceneId: string): string {
  const resolved = (project.scenes || []).find((s: any) => s.id === sceneId)?.motion_direction?.default_provider;
  return resolved || (project.motion_direction?.dance_scene_ids?.includes(sceneId) ? 'kling' : 'wan');
}

export function sceneMotionModelLabel(project: any, sceneId: string): string {
  const scene = (project.scenes || []).find((s: any) => s.id === sceneId);
  const provider = project.motion_direction?.scene_provider_overrides?.[sceneId] || scene?.motion_direction?.provider || recommendedMotionProvider(project, sceneId);
  const row = (project.providers?.video?.capabilities || []).find((r: any) => r.family === provider);
  return row?.label || motionModelNames[provider] || provider;
}

export function motionModelChangeRequest(project: any, sceneId: string, provider: string, acceptSceneIds: string[] = []): any {
  if (!project.motion_direction?.ready) throw new Error('Review motion direction first.');
  if ((project.jobs || []).some((j: any) => ['queued', 'running', 'retrying', 'cancel_requested'].includes(j.status)))
    throw new Error('Wait until the current production job finishes.');
  const scene = (project.scenes || []).find((s: any) => s.id === sceneId);
  const rows = project.providers?.video?.capabilities as {family: string; configured: boolean}[] | undefined;
  // With the model registry (#71) any configured model may be chosen; older servers keep the fixed four.
  const known = rows?.length ? ['default', ...rows.filter((row) => row.configured).map((row) => row.family)] : ['default', 'kling', 'wan', 'seedance', 'grok'];
  if (!scene || !known.includes(provider)) throw new Error('Choose a valid shot and model.');
  if (provider === 'grok') {
    const seconds = Math.ceil((scene.end_ms - scene.start_ms) / 1000);
    if (!Number.isFinite(seconds) || seconds < 1 || seconds > 15) throw new Error('Grok needs a 1–15 second storyboard shot.');
    if (!(rows?.length ? known.includes('grok') : (project.providers?.video?.pool || []).some((p: any) => p.provider === 'replicate-grok' && p.configured)))
      throw new Error('Grok is not configured on this server.');
  }
  if (provider === 'seedance') {
    const seconds = Math.ceil((scene.end_ms - scene.start_ms) / 1000);
    if (!Number.isFinite(seconds) || seconds < 4 || seconds > 15) throw new Error('Seedance needs a 4–15 second storyboard shot.');
    if (!(rows?.length ? known.includes('seedance') : (project.providers?.video?.pool || []).some((p: any) => p.provider === 'replicate-seedance' && p.configured)))
      throw new Error('Seedance is not configured on this server.');
  }
  const overrides = {...project.motion_direction.scene_provider_overrides};
  if (provider === 'default') delete overrides[sceneId]; else overrides[sceneId] = provider;
  return {expected_storyboard_version: project.storyboard_version, expected_revision: project.motion_direction.revision,
    dance_scene_ids: [...project.motion_direction.dance_scene_ids], scene_provider_overrides: overrides, confirm_direction: true,
    ...(acceptSceneIds.length ? {accept_capability_scene_ids: [...acceptSceneIds]} : {})};
}

export interface AnimationStepModel {
  state:'ready'|'running'|'blocked'|'complete';title:string;detail:string;
  action:'animate'|'progress'|'vocals'|'clips'|'blocked';next:number;progress?:number;blockedLabel?:string;
}
/** Server-owned readiness and quotes; this presentation model never approves or generates. */
export function animationNextStepModel(project:MotionProject,completedCount:number):AnimationStepModel|null{
  const scenes=project.scenes||[];
  if(!project.motion_direction||!scenes.length)return null;
  const active=(project.jobs||[]).filter(job=>['queued','running','retrying','cancel_requested'].includes(job.status));
  const job=active.find(job=>['generate_motion_batch','generate_motion'].includes(job.kind));
  const imageWork=project.image_work_count??scenes.filter(s=>!s.image_asset_id||s.continuity_stale).length;
  if(!job&&(!project.storyboard_ready||imageWork>0))return null;
  const quote=project.motion_generation_quote||{},next=quote.scene_ids?.length||0,later=Number(quote.later_count||0),
    completed=Math.max(0,Math.min(scenes.length,completedCount)),
    pendingVocals=scenes.filter(s=>s.vocal_direction?.selected&&s.singing_route!=='avatar'&&(!s.vocal_direction.valid||!s.vocal_direction.audio_confirmed)).length;
  if(job){
    const progress=Number(job.progress);
    return {state:'running',title:job.status==='cancel_requested'?'Animation is stopping safely':job.status==='queued'?'Animation batch is queued':job.status==='retrying'?'Animation batch is retrying':'Animating your storyboard',
      detail:`${completed}/${scenes.length} shots animated. Completed clips stay saved; no second batch will start.`,action:'progress',next,
      progress:job.progress!=null&&Number.isFinite(progress)?Math.max(0,Math.min(100,Math.round(progress))):undefined};
  }
  const blocked=(title:string,detail:string,blockedLabel:string):AnimationStepModel=>({state:'blocked',title,detail,action:'blocked',next,blockedLabel});
  if(active.length)return blocked('Another production job is still running','The animation button will return when that job finishes.','Waiting for the current job');
  if(!project.motion_direction.ready||quote.error)return blocked('Animation needs attention',quote.error||'Review motion direction after rebuilding the storyboard.','Animation not ready');
  if(!quote.ready||!next){
    if(pendingVocals)return {state:'blocked',title:'Confirm the remaining singing shots',detail:`${pendingVocals} singing shots need an audio or selection check before animation.`,action:'vocals',next};
    if(completed===scenes.length)return {state:'complete',title:'Your animations are ready to review',detail:`${completed}/${scenes.length} shots animated. Check the clips below; lip-sync follows for selected singing shots.`,action:'clips',next};
    return blocked('Animation is not ready yet','No animation batch is available. Check the shot details and motion direction below.','No batch available');
  }
  return {state:'ready',title:completed?'Continue animating your storyboard':'Bring your storyboard to life',
    detail:`${completed}/${scenes.length} shots animated · ${next} in the next batch${later?` · ${later} for later`:''}.`,action:'animate',next};
}
